import { useEffect, useSyncExternalStore } from "react";
import { getAxiosInstance } from "@utils/axios-config/axios_config";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import { releaseNotes, ReleaseNote } from "./releaseNotesData";
import {
  DesktopRelease,
  latestPublishedDesktop,
  mergeDesktopReleases,
  parseDesktopCatalog,
} from "./releaseCatalog";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
export const MIN_REFRESH_GAP_MS = 30 * 1000;

interface Snapshot {
  releases: ReleaseNote[];
  loading: boolean;
  latestOffline?: ReleaseNote;
}

let snapshot: Snapshot = { releases: releaseNotes, loading: true };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let lastFetchAt = 0;
let controller: AbortController | null = null;
let timer: number | undefined;

const emit = (next: Snapshot) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

export function refreshReleaseNotes(force = false): Promise<void> {
  if (inFlight) return inFlight;
  if (!force && lastFetchAt && Date.now() - lastFetchAt < MIN_REFRESH_GAP_MS) {
    return Promise.resolve();
  }
  lastFetchAt = Date.now();
  const signal = (controller ??= new AbortController()).signal;
  const request: Promise<void> = (async () => {
    const published: DesktopRelease[] | undefined = await getAxiosInstance()
      .get("/updates/releases/desktop", {
        headers: { Authorization: `Bearer ${SecureTokenManager.getAccessToken()}` },
        signal,
        timeout: 10000,
      })
      .then((response) => parseDesktopCatalog(response.data))
      .catch(() => undefined);
    if (inFlight === request) inFlight = null;
    if (signal.aborted) return;
    if (published) {
      const releases = mergeDesktopReleases(releaseNotes, published);
      emit({
        releases,
        loading: false,
        latestOffline: latestPublishedDesktop(releases, published),
      });
    } else if (snapshot.loading) {
      emit({ ...snapshot, loading: false });
    }
  })();
  inFlight = request;
  return request;
}

const onWake = () => void refreshReleaseNotes();

export function subscribeReleaseNotes(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    timer = window.setInterval(() => void refreshReleaseNotes(true), REFRESH_INTERVAL_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size) return;
    window.removeEventListener("focus", onWake);
    window.removeEventListener("online", onWake);
    window.clearInterval(timer);
    controller?.abort();
    controller = null;
    inFlight = null;
    lastFetchAt = 0;
  };
}

export const getReleaseNotesSnapshot = () => snapshot;

export function useReleaseNotes() {
  const state = useSyncExternalStore(
    subscribeReleaseNotes,
    getReleaseNotesSnapshot,
    getReleaseNotesSnapshot,
  );
  useEffect(() => {
    void refreshReleaseNotes();
  }, []);
  return state;
}

export function resetReleaseNotesForTest() {
  controller?.abort();
  controller = null;
  inFlight = null;
  lastFetchAt = 0;
  snapshot = { releases: releaseNotes, loading: true };
}
